import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import moment from "moment-timezone";

import { getUserCredential } from "src/lib/cookies";

import { TIME_ZONE } from "../utils/payRequestConstants";
import {
  getCalendarPeriods,
  getCompStatementPayGroup,
  getCompStatementResult,
  getPeriodTypes,
} from "../services";

/*
 * Data for the Earnings Overview card (graph1).
 *
 * Web port of the mobile EarningsOverviewContent fetch chain:
 *
 *   1. period types            -> the granularity strip
 *   2. calendar periods        -> which bar starts highlighted
 *   3. comp statement result   -> the bars
 *   4. comp statement paygroup -> the rows under the chart
 *
 * Clicking a bar changes the selected calendar period, which re-runs step 4
 * only. Changing the strip re-runs 2 and 3, and 4 cascades from 2.
 */

const capitalizeFirstLetter = (value) =>
  typeof value === "string" && value.length ? value[0].toUpperCase() + value.slice(1) : value;

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

export const useEarningsOverview = (providerId, parentPeriodTypes = null) => {
  const dispatch = useDispatch();
  const cookiesData = useMemo(() => getUserCredential(), []);

  const [periodTypes, setPeriodTypes] = useState([]);
  const [selectedPeriodType, setSelectedPeriodType] = useState(null);

  const [calendarPeriods, setCalendarPeriods] = useState([]);
  const [selectedCalendar, setSelectedCalendar] = useState(null);

  const [resultRows, setResultRows] = useState([]);
  const [payGroupRows, setPayGroupRows] = useState([]);

  const [isChartLoading, setIsChartLoading] = useState(true);
  const [isPayGroupLoading, setIsPayGroupLoading] = useState(false);

  /*
   * Each fetch stamps a token before awaiting and drops its result if a newer
   * request has started since. Without this, switching the strip twice quickly
   * can let the slower first response overwrite the second.
   */
  const chartRequestRef = useRef(0);
  const payGroupRequestRef = useRef(0);

  // The selected period, read when the bars reload, so a provider switch keeps it.
  const selectedCalendarRef = useRef(null);
  useEffect(() => {
    selectedCalendarRef.current = selectedCalendar;
  }, [selectedCalendar]);

  /* The granularity strip's options, e.g. Month / Quarter / Year. */
  const periodOptions = useMemo(
    () =>
      periodTypes.map((row) => ({
        value: row.period_type_mstr_key,
        label: capitalizeFirstLetter(row.period_type_name),
      })),
    [periodTypes],
  );

  /* 1. Period types - use parent's if provided, otherwise fetch. */
  useEffect(() => {
    // If parent provided period types, use them directly
    if (parentPeriodTypes?.periodOptions?.length) {
      setPeriodTypes(parentPeriodTypes.periodOptions);
      setSelectedPeriodType(parentPeriodTypes.selectedPeriodType);
      return;
    }

    // Otherwise fetch period types
    let cancelled = false;

    (async () => {
      const rows = await getPeriodTypes(cookiesData, dispatch);
      if (cancelled) return;

      setPeriodTypes(rows);
      setSelectedPeriodType(
        rows.length
          ? { id: rows[0].period_type_mstr_key, title: rows[0].period_type_name }
          : null,
      );
      if (!rows.length) setIsChartLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [parentPeriodTypes, cookiesData, dispatch]);

  /* 2 + 3. Calendar periods and the bars, for the selected period type. */
  useEffect(() => {
    if (!selectedPeriodType?.id || !providerId) return undefined;

    const token = chartRequestRef.current + 1;
    chartRequestRef.current = token;
    setIsChartLoading(true);

    (async () => {
      const [periods, results] = await Promise.all([
        getCalendarPeriods(cookiesData, dispatch, selectedPeriodType.id),
        getCompStatementResult(cookiesData, dispatch, providerId, selectedPeriodType.id),
      ]);

      if (chartRequestRef.current !== token) return;

      setCalendarPeriods(periods);
      setResultRows(results);
      const today = moment.tz(TIME_ZONE);
      const periodTypeName = String(selectedPeriodType.title ?? "").toLowerCase();
      const matchingPeriodOption = periodOptions.find((option) =>
        periodTypeName.includes(String(option.label ?? "").toLowerCase())
      );

      const granularity = matchingPeriodOption?.label?.toLowerCase().replace(/ly$/, "");

      const currentPeriod = periods.find((period) => {
        const startDate = moment.tz(period.period_start_date, TIME_ZONE);
        const endDate = moment.tz(period.period_end_date, TIME_ZONE);

        return (
          startDate.isValid() &&
          (granularity
            ? startDate.isSame(today, granularity)
            : endDate.isValid() &&
              startDate.clone().startOf("day").isSameOrBefore(today.clone().startOf("day")) &&
              endDate.clone().endOf("day").isSameOrAfter(today.clone().startOf("day")))
        );
      });
      // A provider switch keeps the selected period; a new period type has other periods, so it falls back.
      const keptPeriod = periods.find((period) => period.period_mstr_key === selectedCalendarRef.current?.id);
      const initialPeriod = keptPeriod || currentPeriod || periods[0];

      // Keeping the same object for a kept period stops the pay group rows loading twice.
      setSelectedCalendar((prev) =>
        initialPeriod && prev?.id === initialPeriod.period_mstr_key
          ? prev
          : initialPeriod
            ? {
                id: initialPeriod.period_mstr_key,
                title: initialPeriod.period_name,
                startDate: initialPeriod.period_start_date,
                endDate: initialPeriod.period_end_date,
              }
            : null,
      );
      setIsChartLoading(false);
    })();

    return () => {
      // Supersede any in-flight response for a period type we've moved off.
      chartRequestRef.current += 1;
    };
  }, [cookiesData, dispatch, periodOptions, providerId, selectedPeriodType]);

  /* 4. Pay group rows for the selected calendar period. */
  useEffect(() => {
    if (!selectedCalendar?.id || !providerId) {
      setPayGroupRows([]);
      return undefined;
    }

    const token = payGroupRequestRef.current + 1;
    payGroupRequestRef.current = token;
    setIsPayGroupLoading(true);

    const projectedValue =
      resultRows.find((row) => row.period_mstr_key === selectedCalendar.id)?.total_projected ?? 0;

    (async () => {
      const rows = await getCompStatementPayGroup(
        cookiesData,
        dispatch,
        providerId,
        selectedCalendar.id,
        projectedValue,
      );

      if (payGroupRequestRef.current !== token) return;

      setPayGroupRows(rows);
      setIsPayGroupLoading(false);
    })();

    return () => {
      payGroupRequestRef.current += 1;
    };
    // resultRows only supplies in_projected_val for the current selection; it is
    // deliberately not a dependency, so refreshing the bars does not refetch the
    // rows a second time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookiesData, dispatch, providerId, selectedCalendar]);

  const chartData = useMemo(
    () =>
      resultRows.map((row) => ({
        category: row.period_name,
        periodKey: row.period_mstr_key,
        projected: round2(row.total_projected),
        earned: round2(row.total_earned),
      })),
    [resultRows],
  );

  /*
   * Mobile drops a series whose values are all zero, so a provider with nothing
   * projected sees a single bar per period instead of one bar and one gap.
   */
  const visibleSeries = useMemo(() => {
    const hasValue = (field) => chartData.some((point) => Number(point[field]) > 0);
    return { projected: hasValue("projected"), earned: hasValue("earned") };
  }, [chartData]);

  /*
   * The chart windows its categories around the selection, so it needs the
   * index rather than just the label. -1 when the selected period has no bar.
   */
  const highlightedIndex = useMemo(() => {
    if (!selectedCalendar?.title) return -1;
    return chartData.findIndex((point) => point.category === selectedCalendar.title);
  }, [chartData, selectedCalendar]);

  const payGroups = useMemo(
    () =>
      payGroupRows.map((row) => ({
        key: row.pay_group_mstr_key,
        name: row.pay_group_name,
        earnedValue: row.earned_value ?? 0,
        projectedValue: row.projected_value ?? 0,
        payGroupType: row.pay_group_type ?? null,
        taskCategoryMstrKey: row.task_category_mstr_key ?? null,
      })),
    [payGroupRows],
  );

  const grandTotal = useMemo(() => {
    if (!payGroups.length) return null;

    return payGroups.reduce(
      (totals, row) => ({
        earnedValue: totals.earnedValue + (row.earnedValue || 0),
        projectedValue: totals.projectedValue + (row.projectedValue || 0),
      }),
      { earnedValue: 0, projectedValue: 0 },
    );
  }, [payGroups]);

  /* Bar click selects that period, which refreshes the rows below. */
  const selectCategory = useCallback(
    (category) => {
      const match = calendarPeriods.find((period) => period.period_name === category);
      if (!match) return;

      setSelectedCalendar({
        id: match.period_mstr_key,
        title: match.period_name,
        startDate: match.period_start_date,
        endDate: match.period_end_date,
      });
    },
    [calendarPeriods],
  );

  const selectPeriodType = useCallback(
    (value) => {
      const match = periodTypes.find((row) => row.period_type_mstr_key === value);
      if (!match) return;

      setSelectedPeriodType({
        id: match.period_type_mstr_key,
        title: match.period_type_name,
      });
    },
    [periodTypes],
  );

  return {
    periodOptions,
    selectedPeriodType: selectedPeriodType?.id ?? null,
    selectPeriodType,

    chartData,
    visibleSeries,
    highlightedCategory: selectedCalendar?.title ?? null,
    highlightedIndex,
    selectCategory,
    selectedPeriodStartDate: selectedCalendar?.startDate ?? null,
    selectedPeriodEndDate: selectedCalendar?.endDate ?? null,

    payGroups,
    grandTotal,

    isChartLoading,
    isPayGroupLoading,
  };
};

export default useEarningsOverview;
